import type * as React from 'react'
import { NavLink } from 'react-router-dom'

import {
  Flex,
  Kbd,
  Sidebar,
  Skeleton,
  SkeletonText,
  Tooltip,
  useSidebar,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import styles from './MainNavigation.module.css'

import type { JSX } from 'react'

type NavigationItemBase = {
  key: string
  label: string
  icon: React.ReactNode
  shortcutKey?: string
  suffix?: React.ReactNode
  selected?: boolean
  decorator?: (children: React.ReactNode) => React.ReactNode
}

type InternalNavigationItem = NavigationItemBase & {
  to: string
  end?: boolean
  href?: never
  external?: never
}

type ExternalNavigationItem = NavigationItemBase & {
  href: string
  external: true
  to?: never
  end?: never
}

/**
 * Placeholder row for an item whose visibility is still unknown (for
 * example, gated on account data that has not loaded). Rendering it keeps
 * the rows below in place instead of shifting when the real item appears.
 */
type SkeletonNavigationItem = {
  key: string
  skeleton: true
}

export type SidebarNavigationItem =
  | InternalNavigationItem
  | ExternalNavigationItem
  | SkeletonNavigationItem

function tooltipWithShortcut(label: string, shortcutKey: string): JSX.Element {
  return (
    <Flex gap="xs" align="center">
      {label}
      <Flex gap="2xs" align="center">
        <Kbd color="inverted">G</Kbd>
        <span className={styles.kbdSeparator}>then</span>
        <Kbd color="inverted">{shortcutKey}</Kbd>
      </Flex>
    </Flex>
  )
}

function NavigationItem({
  item,
  isCollapsed,
}: {
  item: SidebarNavigationItem
  isCollapsed: boolean
}): JSX.Element {
  if ('skeleton' in item) {
    return (
      <Sidebar.ListItem>
        <Sidebar.ButtonSkeleton prefix={<Skeleton width={20} height={20} />}>
          <SkeletonText lines={1} width={72} />
        </Sidebar.ButtonSkeleton>
      </Sidebar.ListItem>
    )
  }

  const tooltipContent = item.shortcutKey
    ? tooltipWithShortcut(item.label, item.shortcutKey)
    : item.label
  const ariaKeyshortcuts = item.shortcutKey
    ? `g ${item.shortcutKey.toLowerCase()}`
    : undefined

  const button = item.external ? (
    <Sidebar.Button
      as="a"
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.mainNavigationLink}
      prefix={item.icon}
      aria-keyshortcuts={ariaKeyshortcuts}
      suffix={item.suffix}
      selected={item.selected}
    >
      {item.label}
      <VisuallyHidden>(opens in a new tab)</VisuallyHidden>
    </Sidebar.Button>
  ) : (
    <Sidebar.Button
      as={NavLink}
      to={item.to}
      end={item.end}
      selected={item.selected}
      className={styles.mainNavigationLink}
      prefix={item.icon}
      aria-keyshortcuts={ariaKeyshortcuts}
      suffix={item.suffix}
    >
      {item.label}
    </Sidebar.Button>
  )

  const tooltip = (
    <Tooltip
      content={tooltipContent}
      side="right"
      open={isCollapsed ? undefined : false}
    >
      {button}
    </Tooltip>
  )

  return (
    <Sidebar.ListItem>
      {item.decorator ? item.decorator(tooltip) : tooltip}
    </Sidebar.ListItem>
  )
}

export function SidebarNavigationList({
  items,
}: {
  items: SidebarNavigationItem[]
}): JSX.Element {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  return (
    <Sidebar.Group className={styles.mainNavigation}>
      <Sidebar.List aria-label="Primary">
        {items.map((item) => (
          <NavigationItem
            key={item.key}
            item={item}
            isCollapsed={isCollapsed}
          />
        ))}
      </Sidebar.List>
    </Sidebar.Group>
  )
}
